import type { PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";

export type PdfMeasureTool = "LENGTH" | "AREA" | "PERIMETER" | "CALIBRATE";
export type PdfMeasureUnits = "IMPERIAL" | "METRIC";

// a point on the sheet in PDF units (1/72 of an inch of paper), unrotated, so
// it stays put through any zoom or rotation
export type PdfPoint = [number, number];

export interface PdfMeasurement {
  id: number;
  tool: Exclude<PdfMeasureTool, "CALIBRATE">;
  pageNumber: number;
  points: PdfPoint[];
}

// how many real-world metres one PDF unit of paper stands for
export interface PdfScale {
  label: string;
  metresPerPoint: number;
}

// two points clicked on a known dimension, waiting for its real length
export interface PdfCalibration {
  pageNumber: number;
  points: [PdfPoint, PdfPoint];
}

const METRES_PER_POINT = 0.0254 / 72;
const FEET_PER_METRE = 3.28084;

// a click that moves less than this is a click, anything more is a pan
export const PDF_CLICK_TOLERANCE = 4;

const ArchitecturalScale = (label: string, feetPerInch: number): PdfScale => ({
  label,
  metresPerPoint: feetPerInch * 12 * METRES_PER_POINT,
});

const RatioScale = (label: string, ratio: number): PdfScale => ({
  label,
  metresPerPoint: ratio * METRES_PER_POINT,
});

// the scales drawings are usually plotted at, grouped as the dropdown shows them
export const PDF_SCALE_PRESETS: { group: string; scales: PdfScale[] }[] = [
  {
    group: "Architectural",
    scales: [
      ArchitecturalScale(`1/16" = 1'-0"`, 16),
      ArchitecturalScale(`3/32" = 1'-0"`, 32 / 3),
      ArchitecturalScale(`1/8" = 1'-0"`, 8),
      ArchitecturalScale(`3/16" = 1'-0"`, 16 / 3),
      ArchitecturalScale(`1/4" = 1'-0"`, 4),
      ArchitecturalScale(`3/8" = 1'-0"`, 8 / 3),
      ArchitecturalScale(`1/2" = 1'-0"`, 2),
      ArchitecturalScale(`3/4" = 1'-0"`, 4 / 3),
      ArchitecturalScale(`1" = 1'-0"`, 1),
      ArchitecturalScale(`1 1/2" = 1'-0"`, 2 / 3),
      ArchitecturalScale(`3" = 1'-0"`, 1 / 3),
    ],
  },
  {
    group: "Engineering",
    scales: [10, 20, 30, 40, 50, 60, 100, 200].map((feet) =>
      ArchitecturalScale(`1" = ${feet}'`, feet),
    ),
  },
  {
    group: "Metric",
    scales: [1, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000].map((ratio) =>
      RatioScale(`1:${ratio}`, ratio),
    ),
  },
];

// until a scale is set the paper itself is measured
export const PDF_FULL_SIZE_SCALE = RatioScale("Not set (paper size)", 1);

const Distance = (from: PdfPoint, to: PdfPoint) =>
  Math.hypot(to[0] - from[0], to[1] - from[1]);

export const PdfPathLength = (points: PdfPoint[], closed: boolean) => {
  let length = 0;
  for (let index = 1; index < points.length; index++) {
    length += Distance(points[index - 1], points[index]);
  }
  if (closed && points.length > 2) {
    length += Distance(points[points.length - 1], points[0]);
  }
  return length;
};

// the shoelace formula, in square PDF units
export const PdfPolygonArea = (points: PdfPoint[]) => {
  let twiceArea = 0;
  for (let index = 0; index < points.length; index++) {
    const [x1, y1] = points[index];
    const [x2, y2] = points[(index + 1) % points.length];
    twiceArea += x1 * y2 - x2 * y1;
  }
  return Math.abs(twiceArea) / 2;
};

// lengths read the way drawings dimension them, to the nearest 1/16", as the
// model viewer writes them
export const FormatPdfLength = (metres: number, units: PdfMeasureUnits) => {
  if (units === "METRIC") {
    return `${metres.toFixed(2)} m`;
  }

  const sixteenths = Math.round(Math.abs(metres) * FEET_PER_METRE * 192);
  const feet = Math.floor(sixteenths / 192);
  const inches = Math.floor((sixteenths % 192) / 16);
  let fraction = sixteenths % 16;
  let denominator = 16;
  while (fraction > 0 && fraction % 2 === 0) {
    fraction /= 2;
    denominator /= 2;
  }
  const inchText = fraction ? `${inches} ${fraction}/${denominator}` : inches;
  return `${feet}' ${inchText}"`;
};

export const FormatPdfArea = (squareMetres: number, units: PdfMeasureUnits) =>
  units === "METRIC"
    ? `${squareMetres.toFixed(2)} m²`
    : `${(squareMetres * FEET_PER_METRE ** 2).toFixed(2)} ft²`;

export const FormatPdfMeasurement = (
  measurement: PdfMeasurement,
  scale: PdfScale,
  units: PdfMeasureUnits,
) =>
  measurement.tool === "AREA"
    ? FormatPdfArea(
        PdfPolygonArea(measurement.points) * scale.metresPerPoint ** 2,
        units,
      )
    : FormatPdfLength(
        PdfPathLength(measurement.points, measurement.tool === "PERIMETER") *
          scale.metresPerPoint,
        units,
      );

/**
 * Reads a typed length into metres. Imperial takes feet and inches the way
 * they are written on drawings (12'-6 1/2", 12' 6", 150", or a bare number of
 * feet); metric takes metres, or a number with m, cm or mm after it.
 */
export const ParsePdfLength = (
  text: string,
  units: PdfMeasureUnits,
): number | null => {
  const input = text.trim().toLowerCase();
  if (input === "") return null;

  const ParseNumber = (value: string) => {
    const [whole, fraction] = value.trim().split(/\s+/);
    const parts = (fraction ?? whole).split("/");
    const fractionValue =
      parts.length === 2 ? Number(parts[0]) / Number(parts[1]) : NaN;
    if (fraction !== undefined) return Number(whole) + fractionValue;
    return parts.length === 2 ? fractionValue : Number(whole);
  };

  let metres: number;
  if (units === "METRIC") {
    const match = input.match(/^([\d.]+)\s*(mm|cm|m)?$/);
    if (!match) return null;
    const factor = { mm: 0.001, cm: 0.01, m: 1 }[match[2] ?? "m"] ?? 1;
    metres = Number(match[1]) * factor;
  } else {
    const match = input.match(
      /^(?:([\d.]+)\s*')?\s*-?\s*(?:([\d.]+(?:\s+\d+\/\d+)?|\d+\/\d+)\s*")?$/,
    );
    if (match && (match[1] || match[2])) {
      const feet = match[1] ? Number(match[1]) : 0;
      const inches = match[2] ? ParseNumber(match[2]) : 0;
      metres = (feet + inches / 12) / FEET_PER_METRE;
    } else if (/^[\d.]+$/.test(input)) {
      metres = Number(input) / FEET_PER_METRE;
    } else {
      return null;
    }
  }

  return Number.isFinite(metres) && metres > 0 ? metres : null;
};

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

// Everything the measure panel shows, pushed to React when it changes.
export interface PdfMeasureState {
  measurements: PdfMeasurement[];
  pageScales: { [pageNumber: number]: PdfScale };
  calibration: PdfCalibration | null;
}

/**
 * Measuring on the sheet. Points are kept in PDF units and drawn into an SVG
 * layer laid over each rendered page, so they follow zoom and rotation, and
 * every page carries its own scale. Nothing is saved: it all goes when the
 * viewer closes.
 */
export class PdfMeasurer {
  private viewer: PDFViewer;
  private onChange: (measureState: Partial<PdfMeasureState>) => void;
  private activeTool: PdfMeasureTool | null = null;
  private units: PdfMeasureUnits = "IMPERIAL";
  private measurements: PdfMeasurement[] = [];
  private pageScales: { [pageNumber: number]: PdfScale } = {};
  private calibration: PdfCalibration | null = null;
  // the measurement being drawn and where the pointer is on its page
  private draft: { pageNumber: number; points: PdfPoint[] } | null = null;
  private hover: PdfPoint | null = null;
  private nextID = 1;

  constructor(
    viewer: PDFViewer,
    onChange: (measureState: Partial<PdfMeasureState>) => void,
  ) {
    this.viewer = viewer;
    this.onChange = onChange;
  }

  get active() {
    return this.activeTool !== null;
  }

  SetActiveTool(tool: PdfMeasureTool | null) {
    this.activeTool = tool;
    this.CancelDraft();
  }

  SetUnits(units: PdfMeasureUnits) {
    this.units = units;
    this.DrawAll();
  }

  ScaleOf(pageNumber: number) {
    return this.pageScales[pageNumber] ?? PDF_FULL_SIZE_SCALE;
  }

  SetPageScale(pageNumber: number, scale: PdfScale) {
    this.pageScales = { ...this.pageScales, [pageNumber]: scale };
    this.onChange({ pageScales: this.pageScales });
    this.DrawAll();
  }

  // the real length of the two points clicked sets the scale of their page
  ApplyCalibration(metres: number) {
    if (!this.calibration) return;

    const paperLength = Distance(...this.calibration.points);
    if (paperLength > 0) {
      this.pageScales = {
        ...this.pageScales,
        [this.calibration.pageNumber]: {
          label: "Calibrated",
          metresPerPoint: metres / paperLength,
        },
      };
    }
    this.calibration = null;
    this.onChange({ pageScales: this.pageScales, calibration: null });
    this.DrawAll();
  }

  CancelCalibration() {
    this.calibration = null;
    this.onChange({ calibration: null });
    this.DrawAll();
  }

  Remove(id: number) {
    this.measurements = this.measurements.filter(
      (measurement) => measurement.id !== id,
    );
    this.onChange({ measurements: this.measurements });
    this.DrawAll();
  }

  Clear() {
    this.measurements = [];
    this.calibration = null;
    this.draft = null;
    this.onChange({ measurements: [], calibration: null });
    this.DrawAll();
  }

  // a new document starts with no measurements and no scales
  Reset() {
    this.pageScales = {};
    this.onChange({ pageScales: {} });
    this.Clear();
  }

  // ---- pointer, called by the scene for clicks that did not pan

  HandleClick(event: MouseEvent) {
    const hit = this.PointAt(event);
    if (!this.activeTool || !hit) return;

    // a point on another page starts over there
    if (this.draft && this.draft.pageNumber !== hit.pageNumber) {
      this.draft = null;
    }
    if (!this.draft) {
      this.draft = { pageNumber: hit.pageNumber, points: [] };
    }

    const points = this.draft.points;
    // A double-click lands twice on the same spot; the second click closes
    // an area or perimeter instead of adding a corner on top of the last.
    const last = points[points.length - 1];
    if (last && event.detail > 1 && this.IsNear(hit, last)) {
      this.FinishDraft();
      return;
    }
    // clicking the first corner again closes the shape
    if (points.length > 2 && this.IsNear(hit, points[0])) {
      this.FinishDraft();
      return;
    }

    points.push(hit.point);
    if (
      (this.activeTool === "LENGTH" || this.activeTool === "CALIBRATE") &&
      points.length === 2
    ) {
      this.FinishDraft();
      return;
    }
    this.DrawPage(hit.pageNumber);
  }

  HandleMove(event: MouseEvent) {
    if (!this.draft) return;

    const hit = this.PointAt(event);
    this.hover =
      hit && hit.pageNumber === this.draft.pageNumber ? hit.point : null;
    this.DrawPage(this.draft.pageNumber);
  }

  HandleKeyDown(event: KeyboardEvent) {
    if (!this.draft) return;

    if (event.key === "Escape") {
      this.CancelDraft();
    } else if (event.key === "Enter") {
      this.FinishDraft();
    } else if (event.key === "Backspace") {
      this.draft.points.pop();
      this.DrawPage(this.draft.pageNumber);
    } else {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  private CancelDraft() {
    const pageNumber = this.draft?.pageNumber;
    this.draft = null;
    this.hover = null;
    if (pageNumber) this.DrawPage(pageNumber);
  }

  private FinishDraft() {
    if (!this.draft || !this.activeTool) return;

    const { pageNumber, points } = this.draft;
    const needed = this.activeTool === "AREA" ? 3 : 2;
    this.draft = null;
    this.hover = null;

    if (points.length >= needed) {
      if (this.activeTool === "CALIBRATE") {
        this.calibration = { pageNumber, points: [points[0], points[1]] };
        this.onChange({ calibration: this.calibration });
      } else {
        this.measurements = [
          ...this.measurements,
          { id: this.nextID++, tool: this.activeTool, pageNumber, points },
        ];
        this.onChange({ measurements: this.measurements });
      }
    }
    this.DrawPage(pageNumber);
  }

  // where on which page the pointer is, in PDF units
  private PointAt(
    event: MouseEvent,
  ): { pageNumber: number; point: PdfPoint; viewportPoint: PdfPoint } | null {
    const pageDiv = (event.target as HTMLElement).closest?.(
      ".page[data-page-number]",
    ) as HTMLElement | null;
    if (!pageDiv) return null;

    const pageNumber = Number(pageDiv.dataset.pageNumber);
    const pageView = this.viewer.getPageView(pageNumber - 1);
    if (!pageView) return null;

    const rect = pageDiv.getBoundingClientRect();
    const viewport = pageView.viewport;
    const viewportPoint: PdfPoint = [
      ((event.clientX - rect.left) * viewport.width) / rect.width,
      ((event.clientY - rect.top) * viewport.height) / rect.height,
    ];
    const point = viewport.convertToPdfPoint(...viewportPoint) as PdfPoint;
    return { pageNumber, point, viewportPoint };
  }

  // close enough on screen to count as the same point
  private IsNear(
    hit: { pageNumber: number; viewportPoint: PdfPoint },
    point: PdfPoint,
  ) {
    const viewport = this.viewer.getPageView(hit.pageNumber - 1).viewport;
    const [x, y] = viewport.convertToViewportPoint(...point);
    return (
      Math.hypot(x - hit.viewportPoint[0], y - hit.viewportPoint[1]) <=
      PDF_CLICK_TOLERANCE * 2
    );
  }

  // ---- drawing

  DrawAll() {
    const pageCount = this.viewer.pagesCount;
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      this.DrawPage(pageNumber);
    }
  }

  /**
   * Redraws one page's layer. pdf.js empties the page's element whenever it
   * draws the page again, so the layer is put back each time.
   */
  DrawPage(pageNumber: number) {
    const pageView = this.viewer.getPageView(pageNumber - 1);
    if (!pageView?.div) return;

    const measurements = this.measurements.filter(
      (measurement) => measurement.pageNumber === pageNumber,
    );
    const draft = this.draft?.pageNumber === pageNumber ? this.draft : null;
    const calibration =
      this.calibration?.pageNumber === pageNumber ? this.calibration : null;

    let layer = pageView.div.querySelector(
      ":scope > .pdfMeasureLayer",
    ) as SVGSVGElement | null;
    if (!measurements.length && !draft && !calibration) {
      layer?.remove();
      return;
    }
    if (!layer) {
      layer = document.createElementNS(SVG_NAMESPACE, "svg");
      layer.classList.add("pdfMeasureLayer");
      pageView.div.appendChild(layer);
    }

    const viewport = pageView.viewport;
    layer.setAttribute("viewBox", `0 0 ${viewport.width} ${viewport.height}`);
    layer.replaceChildren();

    const ToViewport = (points: PdfPoint[]) =>
      points.map(
        (point) => viewport.convertToViewportPoint(...point) as PdfPoint,
      );
    const scale = this.ScaleOf(pageNumber);

    for (const measurement of measurements) {
      this.DrawShape(
        layer,
        ToViewport(measurement.points),
        measurement.tool !== "LENGTH",
        measurement.tool === "AREA",
        FormatPdfMeasurement(measurement, scale, this.units),
        "",
      );
    }

    if (calibration) {
      this.DrawShape(
        layer,
        ToViewport(calibration.points),
        false,
        false,
        "Known length?",
        "pdfMeasureCalibration",
      );
    }

    if (draft && this.activeTool) {
      const points = this.hover ? [...draft.points, this.hover] : draft.points;
      const tool = this.activeTool === "CALIBRATE" ? "LENGTH" : this.activeTool;
      const label =
        points.length > 1 && this.activeTool !== "CALIBRATE"
          ? FormatPdfMeasurement(
              { id: 0, tool, pageNumber, points },
              scale,
              this.units,
            )
          : "";
      this.DrawShape(
        layer,
        ToViewport(points),
        tool !== "LENGTH",
        tool === "AREA",
        label,
        this.activeTool === "CALIBRATE"
          ? "pdfMeasureDraft pdfMeasureCalibration"
          : "pdfMeasureDraft",
      );
    }
  }

  private DrawShape(
    layer: SVGSVGElement,
    points: PdfPoint[],
    closed: boolean,
    filled: boolean,
    label: string,
    className: string,
  ) {
    const group = document.createElementNS(SVG_NAMESPACE, "g");
    group.setAttribute("class", `pdfMeasureShape ${className}`.trim());
    // in the page first, so the label can be measured for its backing
    layer.appendChild(group);

    const path = document.createElementNS(
      SVG_NAMESPACE,
      closed && points.length > 2 ? "polygon" : "polyline",
    );
    path.setAttribute("points", points.map(([x, y]) => `${x},${y}`).join(" "));
    if (filled) path.classList.add("pdfMeasureFill");
    group.appendChild(path);

    for (const [x, y] of points) {
      const handle = document.createElementNS(SVG_NAMESPACE, "circle");
      handle.setAttribute("cx", String(x));
      handle.setAttribute("cy", String(y));
      handle.setAttribute("r", "3");
      group.appendChild(handle);
    }

    if (label && points.length > 1) {
      // a length labels its middle, a shape its centre
      const [x, y] =
        points.length === 2 || !closed
          ? this.Midpoint(points)
          : [
              points.reduce((sum, point) => sum + point[0], 0) / points.length,
              points.reduce((sum, point) => sum + point[1], 0) / points.length,
            ];
      const text = document.createElementNS(SVG_NAMESPACE, "text");
      text.setAttribute("x", String(x));
      text.setAttribute("y", String(y - 10));
      text.textContent = label;
      group.appendChild(text);

      // a dark chip behind the label keeps it readable over linework
      const box = text.getBBox();
      const backing = document.createElementNS(SVG_NAMESPACE, "rect");
      backing.setAttribute("x", String(box.x - 5));
      backing.setAttribute("y", String(box.y - 2));
      backing.setAttribute("width", String(box.width + 10));
      backing.setAttribute("height", String(box.height + 4));
      backing.setAttribute("rx", "3");
      group.insertBefore(backing, text);
    }
  }

  // halfway along the path, so a long polyline's label sits on it
  private Midpoint(points: PdfPoint[]): PdfPoint {
    const half = PdfPathLength(points, false) / 2;
    let walked = 0;
    for (let index = 1; index < points.length; index++) {
      const step = Distance(points[index - 1], points[index]);
      if (walked + step >= half && step > 0) {
        const t = (half - walked) / step;
        return [
          points[index - 1][0] + (points[index][0] - points[index - 1][0]) * t,
          points[index - 1][1] + (points[index][1] - points[index - 1][1]) * t,
        ];
      }
      walked += step;
    }
    return points[0];
  }
}
